import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhoneInput } from './PhoneInput';
import { formatPhoneForTel } from '@/lib/phone-utils';
import { isValidPhoneNumber, getCountryCallingCode, CountryCode } from 'libphonenumber-js';

interface PhoneCallFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

const STORAGE_KEY = 'qr-phone-draft';

export function PhoneCallForm({ contentData, onUpdate }: PhoneCallFormProps) {
  const [phone, setPhone] = useState(contentData.rawPhone || contentData.phone || '');
  const [countryCode, setCountryCode] = useState(contentData.countryCode || 'US');

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.phone && !contentData.phone) setPhone(parsed.phone);
        if (parsed.countryCode) setCountryCode(parsed.countryCode);
      } catch {}
    }
  }, []);

  // Save to localStorage and update parent
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ phone, countryCode }));
    
    const fullPhone = formatPhoneForTel(phone, countryCode);
    onUpdate({
      phone: fullPhone,
      countryCode,
      rawPhone: phone,
    });
  }, [phone, countryCode, onUpdate]);

  // Check if phone is valid
  const isPhoneValid = phone.length > 0 && (() => {
    try {
      const dialCode = getCountryCallingCode(countryCode as CountryCode);
      return isValidPhoneNumber(`+${dialCode}${phone}`);
    } catch {
      return false;
    }
  })();

  const handleTestCall = () => {
    if (!isPhoneValid) return;
    const fullPhone = formatPhoneForTel(phone, countryCode);
    window.open(`tel:${fullPhone}`, '_self');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
        Phone Call
      </h3>

      {/* Phone Input */}
      <PhoneInput
        value={phone}
        onChange={setPhone}
        countryCode={countryCode}
        onCountryChange={setCountryCode}
        label="Phone Number"
        required
        placeholder="Enter phone number"
      />

      {/* Test Call Button */}
      <div className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleTestCall}
          disabled={!isPhoneValid}
          className="w-full gap-2"
        >
          <Phone className="h-4 w-4" />
          Test Phone Call
          <ExternalLink className="h-3 w-3 ml-auto" />
        </Button>
        {!isPhoneValid && phone.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Enter a valid phone number to test the call
          </p>
        )}
      </div>

      {/* Info */}
      <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
        <p className="font-medium mb-1">📞 How it works:</p>
        <p>
          When someone scans this QR code on their phone, it will open the dialer 
          with this number ready to call.
        </p>
      </div>
    </motion.div>
  );
}
